/**
 * ZUNO — planoService.js v2.0
 * ─────────────────────────────────────────────────────────────
 * REGRAS DE NEGÓCIO:
 *
 * ✅ TODO motorista novo tem 30 dias GRÁTIS (independente do plano)
 *    - Durante o trial: NENHUMA cobrança de nada
 *    - Flex no trial: 0% por corrida (grátis total)
 *    - Mensalidade no trial: não paga mensalidade ainda
 *
 * PLANO FLEX (após trial):
 *   - Sem mensalidade
 *   - 8% por corrida → acumulado por dia
 *   - Todo dia às 00:00 → gera cobrança do dia anterior
 *   - Motorista recebe notificação + QR Code Pix para pagar
 *   - Prazo: até meio-dia do próximo dia para pagar
 *   - Se não pagar → bloqueia automaticamente
 *   - Pagou → desbloqueia na hora
 *
 * PLANO MENSALIDADE (após trial):
 *   - Paga mensalidade fixa (MENSALIDADE_VALOR)
 *   - Recebe 100% de cada corrida
 *   - Avisos: 5 dias antes, 2 dias antes, venceu
 *
 * ESTRUTURA FIRESTORE:
 *   driver_planos/{driverUid}          → dados do plano
 *   driver_cobrancas/{driverUid_data}  → cobrança diária Flex
 *   avisos_fila/{autoId}               → fila de notificações
 *   zuno_receita/{autoId}              → receita acumulada da Zuno
 * ─────────────────────────────────────────────────────────────
 */

import {
  doc, getDoc, setDoc, updateDoc,
  collection, addDoc, getDocs,
  query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// ─── CONSTANTES ─────────────────────────────────────────────
export const FLEX_TAXA             = 0.08;   // 8% por corrida
export const TRIAL_DIAS            = 0;      // V1 não possui período promocional
export const MENSALIDADE_VALOR     = 59.90;  // R$ 59,90/mês (ajuste no admin futuramente)
export const PRAZO_PAGAMENTO_HORAS = 12;     // 12h para pagar após cobrança gerada

// ─── HELPERS ────────────────────────────────────────────────
function dataHoje() {
  return new Date().toISOString().slice(0, 10); // "2026-03-31"
}

function dataOntem() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function estaEmTrial(planoData) {
  return false;
}

// ─── ATIVAR PLANO (após aprovação no admin) ─────────────────
// Chamada quando o admin aprova o motorista.
// TODOS os motoristas começam com 30 dias GRÁTIS.
export async function ativarPlano(driverUid, plano, telefone, email, nome) {
  const dados = {
    driverUid,
    plano,            // "mensalidade" | "flex"
    trialAtivo: false,
    trialInicio: null,
    trialVencimento: null,
    planoAtivo: true,
    bloqueado: false,
    motivoBloqueio: null,
    // avisos mensalidade
    avisado5dias: false,
    avisado2dias: false,
    avisadoVenceu: false,
    // contato
    telefone: telefone || "",
    email: email || "",
    nome: nome || "",
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  };

  await setDoc(doc(db, "driver_planos", driverUid), dados);

  await updateDoc(doc(db, "drivers", driverUid), {
    plano,
    trialAtivo: false,
    trialVencimento: null,
    bloqueado: false,
  });

  await _registrarAvisoFila(driverUid, { nome, telefone, email, plano }, "boas_vindas");

  return dados;
}

// ─── BUSCAR PLANO DO MOTORISTA ───────────────────────────────
export async function getPlanoMotorista(driverUid) {
  const ref = doc(db, "driver_planos", driverUid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;

  const data = snap.data();
  const emTrial = estaEmTrial(data);
  const trialVencido = false;

  let status;
  if (data.bloqueado)                                          status = "bloqueado";
  else if (emTrial)                                            status = "trial";
  else if (trialVencido && data.plano === "mensalidade")       status = "vencido";
  else                                                         status = "ativo";

  return { ...data, trialAtivo: false, trialVencimento: null, emTrial, diasRestantes: null, trialVencido, status };
}

// ─── REGISTRAR CORRIDA FLEX (chamar ao finalizar corrida) ────
// Acumula taxa do dia. Durante o trial: GRÁTIS, não registra cobrança.
export async function registrarCorridaFlex(driverUid, valorCorrida, rideId, planoData) {
  const response = await httpsCallable(functions, "recordFlexRideObligation")({ rideId });
  const record = response.data || {};
  const taxa = (record.obligationAmountCents || 0) / 100;
  return { taxa, driverReceive: valorCorrida, emTrial: record.status === "exempt", obligationId: record.rideId || null };
}

export function gerarPixPayload() {
  throw new Error("Pix empresarial da Zuno nao esta configurado no cliente. Gere cobrancas somente pelo backend aprovado.");
}

// ─── GERAR COBRANÇA DIÁRIA (Cloud Function — todo dia 00:00) ─
// Para cada motorista Flex com corridas ontem → gera Pix e notifica
export async function gerarCobrancaDiaria(driverUid) {
  const plano = await getPlanoMotorista(driverUid);

  if (!plano)                     return null;
  if (plano.plano !== "flex")     return null;
  if (plano.emTrial)              return null; // ainda no trial, não cobra
  if (plano.bloqueado)            return null;

  const ontem = dataOntem();
  const docId = `${driverUid}_${ontem}`;
  const cobRef = doc(db, "driver_cobrancas", docId);
  const cobSnap = await getDoc(cobRef);

  if (!cobSnap.exists())                              return null; // não fez corridas
  const cob = cobSnap.data();
  if (cob.status === "cobrado" || cob.status === "pago") return null;
  if (!cob.totalTaxa || cob.totalTaxa <= 0)           return null;

  // Prazo: 24h após a geração da cobrança
  const prazo = new Date();
  prazo.setHours(prazo.getHours() + 24);

  await updateDoc(cobRef, {
    status: "cobrado",
    pixGerado: false,
    pixValor: cob.totalTaxa,
    pixVencimento: prazo.toISOString(),
    pixQrCode: null,
    pixChaveEmpresa: null,
    cobradoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });

  await _registrarAvisoFila(driverUid, plano, "cobranca_flex", {
    valor: cob.totalTaxa,
    corridas: cob.totalCorridas,
    data: ontem,
    pixVencimento: prazo.toISOString(),
  });

  return { ...cob, status: "cobrado", pixValor: cob.totalTaxa };
}

// ─── CONFIRMAR PAGAMENTO (webhook Asaas ou manual no admin) ──
export async function confirmarPagamentoFlex(driverUid, data) {
  const docId = `${driverUid}_${data}`;
  await updateDoc(doc(db, "driver_cobrancas", docId), {
    status: "pago",
    pagoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });

  // Desbloqueia se estava bloqueado por inadimplência
  const planoRef = doc(db, "driver_planos", driverUid);
  const planoSnap = await getDoc(planoRef);
  if (planoSnap.exists() && planoSnap.data().motivoBloqueio === "inadimplencia_flex") {
    await updateDoc(planoRef, {
      bloqueado: false,
      motivoBloqueio: null,
      atualizadoEm: serverTimestamp(),
    });
    await updateDoc(doc(db, "drivers", driverUid), { bloqueado: false });
  }

  const plano = await getPlanoMotorista(driverUid);
  await _registrarAvisoFila(driverUid, plano, "pagamento_confirmado", { data });

  return { sucesso: true };
}

// ─── BLOQUEAR INADIMPLENTE (Cloud Function — todo dia 12:00) ─
export async function bloquearInadimplente(driverUid) {
  const ontem = dataOntem();
  const docId = `${driverUid}_${ontem}`;
  const cobSnap = await getDoc(doc(db, "driver_cobrancas", docId));

  if (!cobSnap.exists()) return null;
  const cob = cobSnap.data();
  if (cob.status === "pago") return null;

  await updateDoc(doc(db, "driver_cobrancas", docId), {
    status: "inadimplente",
    atualizadoEm: serverTimestamp(),
  });

  await updateDoc(doc(db, "driver_planos", driverUid), {
    bloqueado: true,
    motivoBloqueio: "inadimplencia_flex",
    bloqueadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });

  await updateDoc(doc(db, "drivers", driverUid), { bloqueado: true });

  const plano = await getPlanoMotorista(driverUid);
  await _registrarAvisoFila(driverUid, plano, "bloqueio_inadimplencia", {
    valor: cob.totalTaxa,
    data: ontem,
  });

  return { bloqueado: true };
}

// ─── COBRANÇA DE HOJE (para exibir no app do motorista) ──────
export async function getCobrancaHoje(driverUid) {
  const snap = await getDoc(doc(db, "driver_cobrancas", `${driverUid}_${dataHoje()}`));
  return snap.exists() ? snap.data() : null;
}

// ─── COBRANÇAS PENDENTES (para o admin) ─────────────────────
export async function getCobrancasPendentes() {
  const q = query(
    collection(db, "driver_cobrancas"),
    where("status", "in", ["cobrado", "inadimplente"])
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ─── RESUMO FINANCEIRO DO MOTORISTA (admin + extrato) ────────
export async function getResumoFinanceiro(driverUid, mesAno) {
  // mesAno = "2026-03"
  const q = query(
    collection(db, "driver_cobrancas"),
    where("driverUid", "==", driverUid),
    where("data", ">=", `${mesAno}-01`),
    where("data", "<=", `${mesAno}-31`)
  );
  const snap = await getDocs(q);
  const cobrancas = snap.docs.map(d => d.data());

  return {
    mesAno,
    totalCorridas:  cobrancas.reduce((s, c) => s + (c.totalCorridas || 0), 0),
    totalBruto:     +cobrancas.reduce((s, c) => s + (c.totalBruto || 0), 0).toFixed(2),
    totalTaxa:      +cobrancas.reduce((s, c) => s + (c.totalTaxa || 0), 0).toFixed(2),
    totalPago:      +cobrancas.filter(c => c.status === "pago").reduce((s, c) => s + (c.totalTaxa || 0), 0).toFixed(2),
    totalPendente:  +cobrancas.filter(c => c.status !== "pago").reduce((s, c) => s + (c.totalTaxa || 0), 0).toFixed(2),
    diasComCorridas: cobrancas.length,
    cobrancas,
  };
}

// ─── MIGRAR PARA FLEX ────────────────────────────────────────
export async function migrarParaFlex(driverUid) {
  const planoSnap = await getDoc(doc(db, "driver_planos", driverUid));
  const planoAtual = planoSnap.exists() ? planoSnap.data() : {};

  await updateDoc(doc(db, "driver_planos", driverUid), {
    plano: "flex",
    planoAtivo: true,
    trialAtivo: estaEmTrial(planoAtual), // mantém trial se ainda ativo
    migradoParaFlexEm: new Date().toISOString(),
    atualizadoEm: serverTimestamp(),
  });
  await updateDoc(doc(db, "drivers", driverUid), { plano: "flex" });
}

// ─── RENOVAR MENSALIDADE ─────────────────────────────────────
export async function renovarMensalidade(driverUid) {
  const agora = new Date();
  const novoVencimento = new Date(agora);
  novoVencimento.setDate(novoVencimento.getDate() + 30);

  await updateDoc(doc(db, "driver_planos", driverUid), {
    trialAtivo: false,
    planoAtivo: true,
    bloqueado: false,
    motivoBloqueio: null,
    ultimoPagamento: agora.toISOString(),
    proximoVencimento: novoVencimento.toISOString(),
    trialVencimento: novoVencimento.toISOString(),
    avisado5dias: false,
    avisado2dias: false,
    avisadoVenceu: false,
    atualizadoEm: serverTimestamp(),
  });
  await updateDoc(doc(db, "drivers", driverUid), {
    plano: "mensalidade",
    trialAtivo: false,
    trialVencimento: novoVencimento.toISOString(),
    bloqueado: false,
  });
}

// ─── VERIFICAR AVISO DE VENCIMENTO (mensalidade) ─────────────
export async function verificarAvisoVencimento(driverUid) {
  const plano = await getPlanoMotorista(driverUid);
  if (!plano || plano.plano !== "mensalidade") return null;

  // Avisa fim de trial (mesmo para mensalidade)
  if (plano.emTrial && plano.diasRestantes <= 5 && plano.diasRestantes > 0) {
    return { tipo: "trial_fim", diasRestantes: plano.diasRestantes, plano };
  }
  if (plano.emTrial) return null;

  const { diasRestantes, avisado5dias, avisado2dias, avisadoVenceu } = plano;
  const ref = doc(db, "driver_planos", driverUid);

  if (diasRestantes <= 5 && diasRestantes > 2 && !avisado5dias) {
    await updateDoc(ref, { avisado5dias: true, atualizadoEm: serverTimestamp() });
    await _registrarAvisoFila(driverUid, plano, "vencimento_5dias");
    return { tipo: "aviso5dias", diasRestantes, plano };
  }
  if (diasRestantes <= 2 && diasRestantes > 0 && !avisado2dias) {
    await updateDoc(ref, { avisado2dias: true, atualizadoEm: serverTimestamp() });
    await _registrarAvisoFila(driverUid, plano, "vencimento_2dias");
    return { tipo: "aviso2dias", diasRestantes, plano };
  }
  if (diasRestantes <= 0 && !avisadoVenceu) {
    await updateDoc(ref, { avisadoVenceu: true, atualizadoEm: serverTimestamp() });
    await _registrarAvisoFila(driverUid, plano, "vencimento_venceu");
    return { tipo: "venceu", diasRestantes: 0, plano };
  }

  return diasRestantes <= 5 ? { tipo: "lembrete", diasRestantes, plano } : null;
}

// ─── FILA DE AVISOS (privado) ─────────────────────────────────
async function _registrarAvisoFila(driverUid, plano, tipo, extra = {}) {
  const nome = plano?.nome || "Motorista";

  const msgs = {
    boas_vindas: {
      titulo: "🎉 Bem-vindo à Zuno! 30 dias grátis",
      whatsapp: `Olá ${nome}! 🎉 Seu cadastro na *Zuno* foi aprovado!\n\n✅ Você tem *30 dias GRÁTIS* para usar a plataforma sem pagar nada.\n\nApós o trial, continue com o plano que escolheu. Boas corridas! 🚗`,
    },
    cobranca_flex: {
      titulo: `💰 Taxa Zuno: R$ ${extra.valor?.toFixed(2)} — Pague até 12h`,
      whatsapp: `Olá ${nome}! 👋\n\nResumo de ontem (${extra.data}):\n📦 *${extra.corridas} corrida(s)*\n💰 Taxa Zuno (8%): *R$ ${extra.valor?.toFixed(2)}*\n\nPague até *12h de hoje* para continuar ativo. Acesse o app para o QR Code Pix! 📱`,
    },
    bloqueio_inadimplencia: {
      titulo: "❌ Conta bloqueada — Taxa pendente",
      whatsapp: `${nome}, sua conta Zuno foi *bloqueada*. 😔\n\nTaxa pendente: *R$ ${extra.valor?.toFixed(2)}*\n\nPague via Pix no app — *desbloqueio imediato*! ✅`,
    },
    pagamento_confirmado: {
      titulo: "✅ Pagamento confirmado — Conta liberada!",
      whatsapp: `${nome}, pagamento confirmado! ✅\n\nSua conta está *ativa novamente*. Boas corridas! 🚗`,
    },
    vencimento_5dias: {
      titulo: "⚠️ Plano vence em 5 dias",
      whatsapp: `${nome}, seu plano Zuno vence em *5 dias*. Renove no app! 💼`,
    },
    vencimento_2dias: {
      titulo: "🔔 Plano vence em 2 dias",
      whatsapp: `${nome}, atenção! ⏰ Plano vence em *2 dias*. Renove agora ou migre para o Plano Flex!`,
    },
    vencimento_venceu: {
      titulo: "❌ Seu plano venceu",
      whatsapp: `${nome}, seu plano Zuno venceu. Renove ou escolha o *Plano Flex* no app!`,
    },
  };

  const msg = msgs[tipo] || { titulo: tipo, whatsapp: "" };

  await addDoc(collection(db, "avisos_fila"), {
    driverUid,
    tipo,
    telefone: plano?.telefone || "",
    email: plano?.email || "",
    nome,
    titulo: msg.titulo,
    whatsapp: msg.whatsapp,
    extra,
    canais: ["push", "whatsapp"],
    enviado: false,
    criadoEm: serverTimestamp(),
  });
}
